import React from 'react'
import { Modal } from '@/shared/ui/Modal'
import { Button, Input, Label, Radio, Switch } from '@/shared/ui'
import { Affiliate } from '../types'
import { getAffiliateDetails, updateAffiliateById } from '../services/affiliateService'
import AffiliateUplineFields from './AffiliateUplineFields'

type Props = {
  open: boolean
  affiliate: Affiliate | null
  onClose: () => void
  onUpdated?: () => void
}

export default function EditAffiliateModal({ open, affiliate, onClose, onUpdated }: Props) {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [code, setCode] = React.useState('')
  const [commissionMode, setCommissionMode] = React.useState<'percent'|'fixed'>('percent')
  const [commissionValue, setCommissionValue] = React.useState<number>(10)
  const [recurring, setRecurring] = React.useState(true)
  const [parentAffiliateId, setParentAffiliateId] = React.useState<string | null>(null)
  const [uplinePercent, setUplinePercent] = React.useState<number>(0)
  const userId = affiliate?.id || ''

  React.useEffect(() => {
    async function load() {
      if (!open || !affiliate) return
      setLoading(true)
      setError(null)
      try {
        const d = await getAffiliateDetails(affiliate.id)
        setName(d.name || '')
        setEmail(d.email || '')
        setCode(d.code || '')
        setCommissionMode(d.commissionMode)
        setCommissionValue(d.commissionValue)
        setRecurring(d.recurrence === 'recurring')
        setParentAffiliateId(d.parentAffiliateId || null)
        setUplinePercent(d.uplineCommissionPercent || 0)
      } catch (e: any) {
        setError(e?.message ?? 'Falha ao carregar afiliado')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [open, affiliate?.id])

  async function onSave(e: React.FormEvent) {
    e.preventDefault()
    if (!affiliate) return
    setError(null)
    try {
      setLoading(true)
      await updateAffiliateById(userId, {
        code: code.trim(),
        commissionMode,
        commissionValue: Number(commissionValue) || 0,
        recurrence: recurring ? 'recurring' : 'one_time',
        parentAffiliateId,
        uplineCommissionPercent: uplinePercent || 0,
      } as any)
      setLoading(false)
      onClose()
      onUpdated?.()
    } catch (err: any) {
      setLoading(false)
      setError(err?.message ?? 'Falha ao salvar afiliado')
    }
  }

  return (
    <Modal open={open} onOpenChange={(v)=>{ if(!v) onClose() }} className="p-8">
      <div className="mb-4 text-2xl font-semibold tracking-tight">Editar afiliado</div>
      <form className="space-y-6" onSubmit={onSave}>
        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>
        )}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Nome</Label>
            <Input value={name} disabled placeholder="Nome completo" />
          </div>
          <div className="space-y-2">
            <Label>E-mail</Label>
            <Input type="email" value={email} disabled placeholder="email@dominio.com" />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Código de afiliado</Label>
          <Input value={code} onChange={(e)=> setCode(e.target.value)} placeholder="ex.: nome-xyz1" />
          <p className="text-sm text-foreground/70">Este código compõe o link de afiliado (ex.: /ref/codigo).</p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Comissão</Label>
            <div className="flex items-center gap-3">
              <Radio name="mode-edit" checked={commissionMode==='percent'} onChange={()=> setCommissionMode('percent')} label="Percentual (%)" />
              <Radio name="mode-edit" checked={commissionMode==='fixed'} onChange={()=> setCommissionMode('fixed')} label="Valor fixo" />
            </div>
            <Input type="number" min="0" step={commissionMode==='percent'?'0.1':'0.01'} value={commissionValue} onChange={(e)=> setCommissionValue(parseFloat(e.target.value))} />
          </div>
          <div className="space-y-2">
            <Label>Recorrência</Label>
            <div className="flex items-center gap-2">
              <Switch checked={recurring} onChange={(e: React.ChangeEvent<HTMLInputElement>)=> setRecurring(e.currentTarget.checked)} />
              <span className="text-sm">Pagar a cada renovação do plano</span>
            </div>
            <p className="text-sm text-foreground/70">Desligue para pagamento único na primeira venda.</p>
          </div>
        </div>

        <AffiliateUplineFields
          currentAffiliateId={affiliate?.id || null}
          parentAffiliateId={parentAffiliateId}
          onParentChange={setParentAffiliateId}
          uplinePercent={uplinePercent}
          onUplinePercentChange={setUplinePercent}
        />

        <div className="mt-6 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button type="submit" disabled={loading}>{loading ? 'Salvando...' : 'Salvar alterações'}</Button>
        </div>
      </form>
    </Modal>
  )
}
